/**
 * TRUS-709: "open this member's conversation in the Inbox" from anywhere in
 * the app (a workflow step's "Open in Comms Hub", a CSR handoff, an old
 * 2-Way Inbox link).
 *
 * The request is parked here, then the app shell switches to the Inbox
 * (`workspace-messaging`); MessagingWorkspace takes the parked member when it
 * mounts, or hears OPEN_MEMBER_INBOX_EVENT when it is already on screen. The
 * park also survives a reload (sessionStorage) for the URL fallback.
 */

export const OPEN_MEMBER_INBOX_EVENT = 'weavein:open-member-inbox';

/** The app tab that hosts the member Inbox. */
export const MEMBER_INBOX_TAB = 'workspace-messaging';

const STORAGE_KEY = 'member_inbox_open_member';
/** Written by older TypedStepCard builds / tabs; read once, then cleared. */
const LEGACY_PREFILL_KEY = 'comms_hub_prefill';

let pending: number | null = null;

function validId(v: unknown): number | null {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Park a member to open; fires the event for an Inbox already on screen. */
export function requestOpenMemberInbox(memberId: number | string): boolean {
  const id = validId(memberId);
  if (id == null) return false;
  pending = id;
  try {
    sessionStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    /* private mode */
  }
  try {
    window.dispatchEvent(new CustomEvent(OPEN_MEMBER_INBOX_EVENT, { detail: { memberId: id } }));
  } catch {
    /* noop */
  }
  return true;
}

/**
 * The parked member, once (cleared on read). Also accepts the legacy
 * `comms_hub_prefill` payload and a `?member_id=` on an Inbox URL.
 */
export function takePendingMemberInbox(): number | null {
  let id = pending;
  pending = null;
  try {
    const stored = validId(sessionStorage.getItem(STORAGE_KEY));
    sessionStorage.removeItem(STORAGE_KEY);
    id = id ?? stored;
  } catch {
    /* private mode */
  }
  if (id == null) {
    try {
      const raw = localStorage.getItem(LEGACY_PREFILL_KEY);
      if (raw) {
        localStorage.removeItem(LEGACY_PREFILL_KEY);
        id = validId((JSON.parse(raw) as Record<string, unknown> | null)?.member_id);
      }
    } catch {
      /* malformed / private mode */
    }
  }
  if (id == null && typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab === MEMBER_INBOX_TAB || tab === 'inbox' || tab === 'v2-comms') {
        id = validId(params.get('member_id'));
      }
    } catch {
      /* noop */
    }
  }
  return id;
}

/** App tabs that used to be the 2-Way Inbox: they all open the member Inbox now. */
export function redirectLegacyInboxTab(tab: string): string {
  return tab === 'inbox' ? MEMBER_INBOX_TAB : tab;
}
